/**
 * CAMPAIGN ARRIVALS AND THEIR FUNNEL (job #702, Tom 2026-10-13 via Watson: make campaigns such as
 * the India Meta ads through IME visible, from the arrival to the payment).
 *
 * A tab that arrives on the app FROM somewhere — UTM tags on the address, or another site's link —
 * is an arrival. The arrival is sent once, then each later step the same tab reaches, once each:
 * course picked, first lesson started, sign-up, paid. Server half: api/web/campaign.ts.
 *
 * WHAT THIS KEEPS, AND WHERE (Tom's relayed ruling; the counting needs no consent because nothing
 * here can recognise a person):
 *   - The arrival's tags (utm_source, utm_medium, utm_campaign, utm_content, utm_term), the HOST
 *     of the page that linked here (never its path), the landing path without its query, and when
 *     the tab arrived — in sessionStorage, which belongs to this one tab and is gone when it
 *     closes. A new tab tomorrow is a new arrival, or nothing.
 *   - Which steps this tab has already sent, beside them, so each is sent once per tab.
 *   - Each step goes with this page load's throwaway visit id (countingConsent.visitId, memory
 *     only), the device class — mobile or desktop, worked out here and never a user agent — and
 *     the probe flag. NO durable identifier, NO IP (the server keeps the country only), NO
 *     fingerprint.
 *   - No visitor id is ever sent: the counting id (countingConsent.ts) is never joined to an
 *     account. Only the 30-day campaign tag below, stamped at sign-up, reaches an account. A device
 *     that objected to counting sends no step and no stamp.
 *
 * THIRTY DAYS, ACCOUNT-STAMPED (job #184, Tom 2026-10-14: first-party attribution, no banner).
 * The tab's sessionStorage carry above only reaches steps in the same tab. So an arrival with tags
 * is ALSO kept in first-party localStorage for 30 days (KEEP_KEY). The rule is LAST TOUCH within 30 days: a newer tagged link replaces the kept tag (and its
 * arrival time); the same tag reloaded changes nothing; a bare referrer never replaces a UTM tag.
 * When the person signs up (the tab becomes signed in), stampAccount sends the kept tag with the
 * sign-in token to /api/web/campaign-stamp, which stamps the NEW account with it, once. Sign-ups
 * and payments are then counted by that stamp, any day, any tab. The kept tag holds only the tags,
 * the referring host, the landing path and the arrival time — nothing that recognises a person.
 *
 * The capture runs once at boot (main.js), from the real address, BEFORE the router: the
 * picker-first redirect sends a stranger's bare `/` to `/start` without its query, so reading the
 * tags later would lose them.
 */
import { watch, type Ref } from 'vue'
import { countingOff, visitId, store } from '@/composables/countingConsent'
import { CHECKOUT_COMPLETED_EVENT } from '@/lib/checkoutEvents'

export const CARRY_KEY = 'ssi-campaign-carry'
export const FUNNEL_STEPS = ['arrival', 'course_picked', 'lesson_started', 'signup', 'paid'] as const
export type FunnelStep = (typeof FUNNEL_STEPS)[number]
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
type UtmKey = (typeof UTM_KEYS)[number]

/** Our own sites: a link from one of these is moving around, not arriving. */
const OWN_HOST = /(^|\.)saysomethingin\.(app|com)$|(^|\.)ssi-learning-app[a-z0-9-]*\.vercel\.app$|^localhost$|^127\.0\.0\.1$|\.ts\.net$/

export type CampaignTags = Record<UtmKey, string | null> & { referrerHost: string | null; landingPath: string }
export interface Carry extends CampaignTags {
  arrivedAt: string
  sent: FunnelStep[]
}

/** Lowercase, a short safe alphabet, at most 100 characters — the same as the server's cleanTag. */
export function cleanTag(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const s = v.toLowerCase().replace(/[^a-z0-9._+ -]+/g, '_').replace(/\s+/g, ' ').trim().slice(0, 100).trim()
  return s ? s : null
}

/** The host of the page that linked here, or null when there is none or it is one of ours. */
export function referrerHost(referrer: string, ownHost = ''): string | null {
  if (!referrer) return null
  try {
    const u = new URL(referrer)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
    const host = u.hostname.toLowerCase().replace(/^www\./, '')
    if (!host || host === ownHost.toLowerCase().replace(/^www\./, '') || OWN_HOST.test(host)) return null
    return /^[a-z0-9.-]{1,100}$/.test(host) ? host : null
  } catch {
    return null
  }
}

/** The arrival's tags, or null when this load came from nowhere (typed, bookmarked, the installed app, our own pages). */
export function parseArrival(search: string, referrer: string, path: string, ownHost = ''): CampaignTags | null {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search || '')
  } catch {
    params = new URLSearchParams()
  }
  const tags = Object.fromEntries(UTM_KEYS.map((k) => [k, cleanTag(params.get(k))])) as Record<UtmKey, string | null>
  const host = referrerHost(referrer, ownHost)
  if (!UTM_KEYS.some((k) => tags[k]) && !host) return null
  return { ...tags, referrerHost: host, landingPath: (path.split(/[?#]/)[0] || '/').slice(0, 120) }
}

/** Mobile or desktop, from the screen and the pointer — one bit, never the user agent string. */
export function deviceClass(w: { matchMedia?: (q: string) => { matches: boolean }; screen?: { width: number; height: number } } | null = typeof window !== 'undefined' ? window : null): 'mobile' | 'desktop' | null {
  if (!w) return null
  try {
    const coarse = !!w.matchMedia?.('(pointer: coarse)').matches
    const short = w.screen ? Math.min(w.screen.width, w.screen.height) : Infinity
    return coarse || short < 600 ? 'mobile' : 'desktop'
  } catch {
    return null
  }
}

type SessionStore = Pick<Storage, 'getItem' | 'setItem'>
type KeepStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
function session(): SessionStore | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null
  } catch {
    return null
  }
}

export const KEEP_KEY = 'ssi-campaign-30d'
export const KEEP_MS = 30 * 24 * 3_600_000
export interface Kept extends CampaignTags {
  /** When this tag was first seen on this device (the newest tagged arrival). */
  arrivedAt: string
  /** The server has stamped an account with it. Alone (no account known) it stops further sends. */
  stamped?: boolean
  /** The accounts this device has already settled with the server (stamped, already stamped, or
   *  not new). A DIFFERENT new account on a shared device is still stamped (lanes review, 805d6ff). */
  stampedFor?: string[]
}

/** The account a session token belongs to (its JWT `sub`), for the per-account flag only. */
function accountOf(token: string): string | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: unknown }
    return typeof claims.sub === 'string' ? claims.sub : null
  } catch {
    return null
  }
}

function device30(): KeepStore | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    return null
  }
}

/** The kept campaign, or null when there is none, it is older than 30 days, or it is malformed. */
export function readKept(l: KeepStore | null = device30(), now = Date.now()): Kept | null {
  try {
    const k = JSON.parse(l?.getItem(KEEP_KEY) || 'null') as Kept | null
    if (!k || typeof k.arrivedAt !== 'string') return null
    const t = Date.parse(k.arrivedAt)
    if (!Number.isFinite(t) || now - t > KEEP_MS) return null
    return k
  } catch {
    return null
  }
}

function writeKept(k: Kept, l: KeepStore | null = device30()): void {
  try {
    l?.setItem(KEEP_KEY, JSON.stringify(k))
  } catch {
    /* storage full or blocked: the session carry still serves this tab */
  }
}

const hasUtm = (t: CampaignTags): boolean => UTM_KEYS.some((k) => t[k])
const sameTags = (a: CampaignTags, b: CampaignTags): boolean => UTM_KEYS.every((k) => a[k] === b[k]) && a.referrerHost === b.referrerHost

/** Keep an arrival's tags for 30 days, last touch winning. Returns what is kept afterwards. */
export function keepArrival(tags: CampaignTags, l: KeepStore | null = device30(), now = Date.now()): Kept | null {
  const old = readKept(l, now)
  if (old && hasUtm(old) && !hasUtm(tags)) return old
  if (old && sameTags(old, tags)) return old
  const k: Kept = { ...tags, arrivedAt: new Date(now).toISOString() }
  writeKept(k, l)
  return k
}

export function readCarry(s: SessionStore | null = session()): Carry | null {
  try {
    const c = JSON.parse(s?.getItem(CARRY_KEY) || 'null') as Carry | null
    return c && typeof c.arrivedAt === 'string' && Array.isArray(c.sent) ? c : null
  } catch {
    return null
  }
}

function writeCarry(c: Carry, s: SessionStore | null = session()): void {
  try {
    s?.setItem(CARRY_KEY, JSON.stringify(c))
  } catch {
    /* private mode: the arrival still counts, a later step may not */
  }
}

function isProbe(): boolean {
  try {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return true
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).get('probe') === '1') return true
    return store()?.getItem('ssi_probe') === '1'
  } catch {
    return false
  }
}

export interface StepBody extends CampaignTags {
  step: FunnelStep
  arrivedAt: string
  visitId: string
  device: 'mobile' | 'desktop' | null
  probe: boolean
}

/** What one step sends: the carry's tags and nothing that recognises a person. */
export function stepBody(step: FunnelStep, carry: Carry): StepBody {
  const tags: Omit<Carry, 'sent'> & { sent?: unknown } = { ...carry }
  delete tags.sent
  return { ...tags, step, visitId: visitId(), device: deviceClass(), probe: isProbe() }
}

/** Where a signed-in tab gets its token from; App.vue sets it. A step goes without one when there is none. */
let tokenSource: (() => Promise<string | null>) | null = null
export function setCampaignTokenSource(fn: (() => Promise<string | null>) | null): void {
  tokenSource = fn
}

type Post = (url: string, init: RequestInit) => Promise<{ ok: boolean; json?: () => Promise<unknown> }>
const defaultPost: Post = (url, init) => fetch(url, init)

async function send(body: StepBody, post: Post, token: string | null): Promise<boolean> {
  if (countingOff()) return false
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
    const r = await post('/api/web/campaign', { method: 'POST', headers, body: JSON.stringify(body), keepalive: true })
    return r.ok
  } catch {
    return false
  }
}

/**
 * At boot: is this tab an arrival? A tab that already carries one (a reload, a move inside the
 * app) is not a new arrival. Returns the carry, or null when the tab came from nowhere.
 */
export function captureCampaignArrival(opts: { search?: string; referrer?: string; path?: string; host?: string; post?: Post; s?: SessionStore | null; l?: KeepStore | null; now?: number } = {}): Carry | null {
  const s = opts.s === undefined ? session() : opts.s
  const existing = readCarry(s)
  const tags = parseArrival(
    opts.search ?? (typeof location !== 'undefined' ? location.search : ''),
    opts.referrer ?? (typeof document !== 'undefined' ? document.referrer : ''),
    opts.path ?? (typeof location !== 'undefined' ? location.pathname : '/'),
    opts.host ?? (typeof location !== 'undefined' ? location.hostname : ''),
  )
  if (tags) keepArrival(tags, opts.l === undefined ? device30() : opts.l, opts.now ?? Date.now())
  if (existing) return existing
  if (!tags) return null
  const carry: Carry = { ...tags, arrivedAt: new Date(opts.now ?? Date.now()).toISOString(), sent: ['arrival'] }
  writeCarry(carry, s)
  void send(stepBody('arrival', carry), opts.post ?? defaultPost, null)
  return carry
}

/**
 * A later step this tab reached. Nothing when the tab did not arrive from somewhere, or when the
 * step was already sent. A sign-up goes with the sign-in token, because the server decides
 * whether the account is really new since the arrival; the token is read and never stored.
 */
export async function noteFunnelStep(step: Exclude<FunnelStep, 'arrival'>, opts: { post?: Post; s?: SessionStore | null; token?: string | null } = {}): Promise<boolean> {
  const s = opts.s === undefined ? session() : opts.s
  const carry = readCarry(s)
  if (!carry || carry.sent.includes(step)) return false
  carry.sent = [...carry.sent, step]
  writeCarry(carry, s)
  let token = opts.token ?? null
  if (token == null && tokenSource) {
    try {
      token = await tokenSource()
    } catch {
      token = null
    }
  }
  if (step === 'signup' && !token) {
    // No sign-in to check: take the step back so the real sign-up can still send it.
    carry.sent = carry.sent.filter((x) => x !== step)
    writeCarry(carry, s)
    return false
  }
  return send(stepBody(step, carry), opts.post ?? defaultPost, token)
}

/**
 * At sign-up: stamp the NEW account with the campaign this device kept (or, with no device storage,
 * this tab's carry). Once per page load; marked done only when the server stamped or had already.
 * A returning learner signing in is told "not a new account": only THAT account is marked settled, so a real
 * sign-up later on the same device can still be stamped within the 30 days.
 */
let stampTried = false
export async function stampAccount(opts: { post?: Post; l?: KeepStore | null; s?: SessionStore | null; token?: string | null; now?: number; force?: boolean } = {}): Promise<boolean> {
  if (stampTried && !opts.force) return false
  if (countingOff()) return false
  const l = opts.l === undefined ? device30() : opts.l
  const kept = readKept(l, opts.now ?? Date.now())
  const carry = kept ? null : readCarry(opts.s === undefined ? session() : opts.s)
  const from: CampaignTags & { arrivedAt: string; stamped?: boolean; stampedFor?: string[] } | null = kept ?? carry
  if (!from) return false
  let token = opts.token ?? null
  if (token == null && tokenSource) {
    try {
      token = await tokenSource()
    } catch {
      token = null
    }
  }
  if (!token) return false
  const account = accountOf(token)
  const settled = from.stampedFor ?? []
  if (account ? settled.includes(account) : from.stamped) return false
  stampTried = true
  const rest: Record<string, unknown> = { ...from }
  delete rest.stamped
  delete rest.stampedFor
  delete rest.sent
  try {
    const r = await (opts.post ?? defaultPost)('/api/web/campaign-stamp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...rest, device: deviceClass(), probe: isProbe() }),
      keepalive: true,
    })
    if (!r.ok) return false
    const a = (await r.json?.().catch(() => null)) as { stamped?: boolean; reason?: string } | null
    const done = a?.stamped || a?.reason === 'already stamped'
    if (kept && (done || (account && a?.reason === 'not a new account'))) {
      writeKept({ ...kept, ...(done ? { stamped: true } : {}), ...(account ? { stampedFor: [...settled, account] } : {}) }, l)
    }
    return !!a?.stamped
  } catch {
    return false
  }
}

/**
 * App-level (in App.vue): the token source for every step, the account
 * stamp when this tab becomes signed in (the sign-up, counted by the stamp from job #184), and the payment when Paddle says a checkout completed in this
 * page (the same in-page signal useSubscription converges on).
 */
export function watchCampaignFunnel(isAuthenticated: Ref<boolean>, getToken: () => Promise<string | null>): void {
  setCampaignTokenSource(getToken)
  watch(isAuthenticated, (yes) => { if (yes) void stampAccount() }, { immediate: true })
  if (typeof window !== 'undefined') window.addEventListener(CHECKOUT_COMPLETED_EVENT, () => { void noteFunnelStep('paid') })
}
